import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  CloudDownload as CloudDownloadIcon,
  Sync as SyncIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdminUsuarios, useImportJob } from '../hooks/useAdminUsuarios';
import { CriarUsuarioDialog } from '../components/CriarUsuarioDialog';
import { AlterarSenhaDialog } from '../components/AlterarSenhaDialog';
import { PoliticasGlobaisPage } from './PoliticasGlobaisPage';
import api from '../../../services/auth.service';

// AIDEV-NOTE: Página principal de administração com 2 abas de nível superior:
// "Usuários" (CRUD de vendedores) e "Políticas Globais" (configurações comerciais padrão)

const PERFIL_COLOR: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  admin: 'primary',
  gerente: 'secondary',
  vendedor: 'success',
  representante: 'warning'
};

export function UsuariosPage() {
  const navigate = useNavigate();
  const {
    data: usuarios,
    isLoading,
    error,
    createMutation,
    toggleStatusMutation,
    syncPedidosErpMutation
  } = useAdminUsuarios();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [mainTab, setMainTab] = useState(0);
  const [syncingVendedor, setSyncingVendedor] = useState<number | null>(null);
  const [syncSnackbar, setSyncSnackbar] = useState<string | null>(null);
  const [syncingClientes, setSyncingClientes] = useState(false);
  const [senhaDialog, setSenhaDialog] = useState<{ id: string; nome: string } | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const jobQuery = useImportJob(activeJobId);

  // AIDEV-NOTE: reage à conclusão do polling job — atualiza snackbar e libera botão
  useEffect(() => {
    if (!jobQuery.data) return;
    if (jobQuery.data.status === 'completed') {
      const r = jobQuery.data.result!;
      setSyncSnackbar(
        `Importação concluída: ${r.imported} importado(s), ${r.skipped} já existente(s)${r.errors > 0 ? `, ${r.errors} erro(s)` : ''}`
      );
      setSyncingVendedor(null);
      setActiveJobId(null);
    } else if (jobQuery.data.status === 'failed') {
      setSyncSnackbar(`Erro na importação: ${jobQuery.data.error}`);
      setSyncingVendedor(null);
      setActiveJobId(null);
    } else if (jobQuery.data.status === 'running' && jobQuery.data.progress.total > 0) {
      const { processed, total } = jobQuery.data.progress;
      setSyncSnackbar(`Importando… ${processed}/${total} pedidos`);
    }
  }, [jobQuery.data]);

  const handleCreate = async (payload: any) => {
    try {
      setCreateError(null);
      await createMutation.mutateAsync(payload);
      setDialogOpen(false);
    } catch (err: any) {
      setCreateError(err?.response?.data?.error ?? String(err));
    }
  };

  const handleSyncClientes = async () => {
    setSyncingClientes(true);
    try {
      // AIDEV-NOTE: force=true reseta o delta timestamp para re-sync completo de clientes
      // Necessário quando o mapeamento de dados mudou (ex: city codes → city names via JOIN CIDADE)
      await api.post('/sync/trigger', { entity: 'clientes', force: true });
      setSyncSnackbar(
        'Sincronização completa de clientes iniciada. Os nomes de cidades serão atualizados em breve.'
      );
    } catch (err: any) {
      const detail = err?.response?.data?.error ?? err?.response?.data?.message ?? String(err);
      setSyncSnackbar(`Erro ao sincronizar clientes: ${detail}`);
    } finally {
      setSyncingClientes(false);
    }
  };

  const handleSyncPedidos = async (vendedorErpId: number, vendedorNome: string) => {
    setSyncingVendedor(vendedorErpId);
    setActiveJobId(null);
    try {
      const { jobId } = await syncPedidosErpMutation.mutateAsync(vendedorErpId);
      setActiveJobId(jobId);
      setSyncSnackbar(`Importando pedidos de ${vendedorNome}…`);
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? err?.response?.data?.error ?? String(err);
      setSyncSnackbar(`Erro ao iniciar importação de ${vendedorNome}: ${detail}`);
      setSyncingVendedor(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Configurações
      </Typography>

      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Usuários" />
        <Tab label="Políticas Globais" />
      </Tabs>

      {/* Aba Usuários */}
      {mainTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
            <Tooltip title="Atualiza nomes de cidades no mapa (executa sync de clientes do ERP)">
              <span>
                <Button
                  variant="outlined"
                  startIcon={syncingClientes ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={handleSyncClientes}
                  disabled={syncingClientes}
                >
                  Sincronizar Clientes
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Novo Usuário
            </Button>
          </Box>

          {isLoading && <CircularProgress />}
          {error && <Alert severity="error">Erro ao carregar usuários</Alert>}

          {usuarios && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Perfil</TableCell>
                  <TableCell>Vendedor ERP</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.nome}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.perfil}
                        color={PERFIL_COLOR[u.perfil] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{u.vendedor_erp_id ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.ativo ? 'Ativo' : 'Inativo'}
                        color={u.ativo ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {/* AIDEV-NOTE: isPending global desabilita todos os toggles — evita cliques duplos concorrentes */}
                      <Tooltip title="Alterar senha">
                        <IconButton
                          size="small"
                          onClick={() => setSenhaDialog({ id: u.id, nome: u.nome })}
                        >
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        title={u.ativo ? 'Inativar usuário' : 'Ativar usuário'}
                        onClick={() => toggleStatusMutation.mutate({ id: u.id, ativo: !u.ativo })}
                        disabled={toggleStatusMutation.isPending}
                      >
                        {u.ativo ? (
                          <ToggleOnIcon fontSize="small" color="success" />
                        ) : (
                          <ToggleOffIcon fontSize="small" color="disabled" />
                        )}
                      </IconButton>
                      {u.vendedor_erp_id && (
                        <>
                          <Tooltip
                            title={
                              syncingVendedor === u.vendedor_erp_id &&
                              jobQuery.data?.status === 'running' &&
                              jobQuery.data.progress.total > 0
                                ? `${jobQuery.data.progress.processed}/${jobQuery.data.progress.total} pedidos`
                                : 'Importar pedidos históricos do ERP'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleSyncPedidos(u.vendedor_erp_id!, u.nome)}
                                disabled={syncingVendedor !== null}
                              >
                                {syncingVendedor === u.vendedor_erp_id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" color="primary" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            size="small"
                            title="Configurações do vendedor"
                            onClick={() => navigate(`/admin/vendedores/${u.vendedor_erp_id}`)}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* AIDEV-PERF: Monta dialog apenas quando aberto — evita 6 queries de lookup no mount */}
          {dialogOpen && (
            <CriarUsuarioDialog
              open={dialogOpen}
              onClose={() => {
                setDialogOpen(false);
                setCreateError(null);
              }}
              onSubmit={handleCreate}
              isLoading={createMutation.isPending}
              error={createError}
            />
          )}
          {senhaDialog && (
            <AlterarSenhaDialog
              open={!!senhaDialog}
              usuarioId={senhaDialog.id}
              usuarioNome={senhaDialog.nome}
              onClose={() => setSenhaDialog(null)}
              onSuccess={() => {
                setSenhaDialog(null);
                setSyncSnackbar('Senha alterada com sucesso');
              }}
            />
          )}
        </Box>
      )}

      {/* Aba Políticas Globais — renderiza o componente existente */}
      {mainTab === 1 && <PoliticasGlobaisPage />}

      <Snackbar
        open={!!syncSnackbar}
        autoHideDuration={6000}
        onClose={() => setSyncSnackbar(null)}
        message={syncSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
